/**
 * Standardized API Service Utility
 * 
 * This utility provides a consistent way to handle API calls with proper error handling,
 * response validation, and standardized error messages.
 */

import axios, { deduplicatedInstance } from '@configs/axios';
import { handleApiError, normalizeError } from './errorHandling';

/**
 * API Response Error - Custom error class for API responses
 */
export class ApiResponseError extends Error {
  constructor(message, status = 500, data = null, originalError = null) {
    super(message);
    this.name = 'ApiResponseError';
    this.status = status;
    this.data = data;
    this.originalError = originalError;
  }
}

/**
 * Validate API response structure
 * @param {Object} response - Axios response object
 * @param {Object} options - Validation options
 * @returns {Object} Validated response data
 */
const validateResponse = (response, options = {}) => {
  const { 
    requireData = true, 
    expectedStatus = [200, 201], 
    dataPath = 'data' 
  } = options;

  // Check if response exists
  if (!response) {
    throw new ApiResponseError('No response received from server', 0);
  }

  // Check status code
  const statusArray = Array.isArray(expectedStatus) ? expectedStatus : [expectedStatus];
  if (!statusArray.includes(response.status)) {
    throw new ApiResponseError(
      `Unexpected status code: ${response.status}`,
      response.status,
      response.data
    );
  }

  // Check for API-level error codes (some APIs return 200 but with error codes in data)
  if (response.data?.code && response.data.code !== 200) {
    throw new ApiResponseError(
      response.data.error || response.data.message || 'API returned error code',
      response.data.code,
      response.data
    );
  }

  // Validate data presence if required
  if (requireData) {
    const responseData = dataPath ? response.data?.[dataPath] : response.data;
    if (!responseData && responseData !== 0 && responseData !== false) {
      throw new ApiResponseError('No data received from server', response.status, response.data);
    }
  }

  return response.data;
};

/**
 * Create a standardized API service function
 * @param {Function} apiCall - The axios API call function
 * @param {Object} options - Configuration options
 * @returns {Function} Wrapped API service function
 */
export const createApiService = (apiCall, options = {}) => {
  const {
    context = 'API Request',
    validateOptions = {},
    transformResponse = (data) => data,
    onSuccess,
    onError
  } = options;

  return async (...args) => {
    try {
      const response = await apiCall(...args);

      // Validate response
      const validatedData = validateResponse(response, validateOptions);

      // Transform response if needed
      const transformedData = transformResponse(validatedData);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(transformedData, response);
      }

      return transformedData;

    } catch (error) {
      // Skip error handling if this is a token expiration error
      if (error._handledByTokenExpiration) {
        console.log(`[${context}] Skipping error handling - token expiration in progress`);
        throw error;
      }

      // Normalize and handle error
      const normalizedError = normalizeError(error);

      // Call error callback if provided
      if (onError) {
        onError(normalizedError, error);
      }

      // Log error with context (but suppress 401 errors as they're handled by jwtService)
      if (normalizedError.status !== 401) {
        console.error(`[${context}] API Error:`, normalizedError);
      }

      // Re-throw as ApiResponseError for consistent error handling
      if (error instanceof ApiResponseError) {
        throw error;
      }

      // Create new ApiResponseError but preserve original error properties
      const apiError = new ApiResponseError(
        normalizedError.message,
        normalizedError.status,
        normalizedError.data,
        error
      );

      // Preserve custom properties from original error (like _handledByCustomLogic, _handledByTokenExpiration)
      if (error && typeof error === 'object') {
        Object.keys(error).forEach(key => {
          if (key.startsWith('_') && !(key in apiError)) {
            apiError[key] = error[key];
          }
        });
      }

      // Preserve response object for mutation error handlers
      if (error?.response) {
        apiError.response = error.response;
      }

      throw apiError;
    }
  };
};

/**
 * Common API service configurations
 */
export const API_CONFIGS = {
  // Standard GET request that expects data
  GET_WITH_DATA: {
    validateOptions: { requireData: true },
    context: 'GET Request'
  },
  
  // GET request that may not have data (like status checks)
  GET_OPTIONAL_DATA: {
    validateOptions: { requireData: false },
    context: 'GET Request'
  },
  
  // POST request for creation
  POST_CREATE: {
    validateOptions: { expectedStatus: [200, 201], requireData: true },
    context: 'POST Create'
  },
  
  // POST request for actions (may not return data)
  POST_ACTION: {
    validateOptions: { expectedStatus: [200, 201], requireData: false },
    context: 'POST Action'
  },
  
  // PUT/PATCH request for updates
  PUT_UPDATE: {
    validateOptions: { expectedStatus: [200, 204], requireData: false },
    context: 'PUT Update'
  },
  
  // DELETE request
  DELETE: {
    validateOptions: { expectedStatus: [200, 204], requireData: false },
    context: 'DELETE Request'
  }
};

/**
 * Quick helper functions for common API patterns
 */
export const apiGet = (url, config = {}) =>
  createApiService(() => deduplicatedInstance.get(url, config), API_CONFIGS.GET_WITH_DATA);

export const apiPost = (url, data = {}, config = {}) =>
  createApiService(() => axios.post(url, data, config), API_CONFIGS.POST_CREATE);

export const apiPut = (url, data = {}, config = {}) =>
  createApiService(() => axios.put(url, data, config), API_CONFIGS.PUT_UPDATE);

export const apiDelete = (url, config = {}) =>
  createApiService(() => axios.delete(url, config), API_CONFIGS.DELETE);

export default {
  createApiService,
  validateResponse,
  ApiResponseError,
  API_CONFIGS,
  apiGet,
  apiPost,
  apiPut,
  apiDelete
};
